// src/pages/OAuthCallbackPage.tsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { Container, Box, Typography } from '@mui/material';
import { Spinner } from '@chakra-ui/react';
import { theme } from '../../constants/theme';
import { useThemeContext } from '../../hook/useThemeManager';
import { oauthCancelMessage, parseOAuthCallbackSearch } from '../../utils/oauthCallbackMessages';

const OAuthCallbackPage = () => {
    const { provider } = useParams();
    const location = useLocation();
    const { themeStyle } = useThemeContext();
    const parsed = useMemo(() => parseOAuthCallbackSearch(location.search), [location.search]);
    const [statusMessage, setStatusMessage] = useState(() =>
        parsed.cancelled
            ? oauthCancelMessage(provider)
            : parsed.code
            ? `Logging in via ${provider}...`
            : 'Authorization incomplete. You can close this window and try again.',
    );

    const notifyOpener = useCallback(() => {
        const targetOrigin = window.location.origin;

        if (parsed.code && provider && !parsed.error) {
            window.opener?.postMessage(
                {
                    type: 'SOCIAL_AUTH_SUCCESS',
                    provider,
                    code: parsed.code,
                },
                targetOrigin,
            );
            window.close();
            return;
        }

        const message = parsed.cancelled
            ? oauthCancelMessage(provider)
            : 'Authorization failed. You can close this window and try again.';
        setStatusMessage(message);

        window.opener?.postMessage(
            {
                type: 'SOCIAL_AUTH_FAILED',
                provider,
                error: parsed.error,
                error_description: parsed.error_description,
                error_reason: parsed.error_reason,
                cancelled: parsed.cancelled,
                message,
            },
            targetOrigin,
        );

        // Brief delay so cancel copy is visible in the popup (screencast / UX)
        window.setTimeout(() => window.close(), parsed.cancelled ? 1800 : 800);
    }, [parsed, provider]);

    useEffect(() => {
        notifyOpener();
    }, [notifyOpener]);

    return (
        <Container
            sx={{
                display: 'flex',
                height: '100vh',
                width: '100vw',
                alignItems: 'center',
                justifyContent: 'center',
            }}>
            <Box flexDirection="column" alignItems="center" justifyItems="center" sx={{ textAlign: 'center', px: 3 }}>
                {!parsed.cancelled && parsed.code ? (
                    <Spinner color={theme[themeStyle].tag_white} height={100} width={100} />
                ) : null}
                <Typography variant="h6" mt={2} sx={{ color: theme[themeStyle].tag_white, maxWidth: 420 }}>
                    {statusMessage}
                </Typography>
            </Box>
        </Container>
    );
};

export { OAuthCallbackPage };
